import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, AlertTriangle, Shield, Clock, CheckCircle, Eye, 
  Filter, Flame, Droplets, Wrench, AlertCircle 
} from 'lucide-react';

// Get current datetime in IST for datetime-local input
const getISTDateTime = () => {
  const now = new Date();
  return now.toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata' }).replace(' ', 'T').slice(0, 16);
};

const severityColors = {
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

const statusColors = {
  open: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  investigating: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
};

const categoryIcons = {
  'Fuel Spill': Droplets,
  'Fire': Flame,
  'Equipment Failure': Wrench,
  'Theft': AlertCircle,
  default: AlertTriangle
};

const categories = [
  'Fuel Spill', 'Fire', 'Equipment Failure', 'Theft', 'Accident',
  'Environmental', 'Safety Violation', 'Customer Complaint', 'Other'
];

function IncidentsTab() {
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  const [formData, setFormData] = useState({
    category: 'Other',
    severity: 'medium',
    title: '',
    description: '',
    location_detail: '',
    occurred_at: getISTDateTime(),
    witnesses: '',
    immediate_action: '',
    estimated_loss: 0
  });

  const [updateData, setUpdateData] = useState({
    status: '',
    root_cause: '',
    corrective_action: ''
  });

  useEffect(() => {
    fetchIncidents();
    fetchStats();
  }, [statusFilter, severityFilter]);

  const fetchIncidents = async () => {
    try {
      let params = [];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (severityFilter !== 'all') params.push(`severity=${severityFilter}`);
      const query = params.length > 0 ? `?${params.join('&')}` : '';
      
      const res = await axios.get(`${API}/incidents${query}`);
      setIncidents(res.data.incidents);
    } catch (error) {
      toast.error('Failed to load incidents');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/incidents/summary/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchIncidentDetail = async (incidentId) => {
    try {
      const res = await axios.get(`${API}/incidents/${incidentId}`);
      setSelectedIncident(res.data.incident);
      setUpdateData({
        status: res.data.incident.status,
        root_cause: res.data.incident.root_cause || '',
        corrective_action: res.data.incident.corrective_action || ''
      });
      setDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load incident details');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        occurred_at: new Date(formData.occurred_at).toISOString(),
        witnesses: formData.witnesses ? formData.witnesses.split(',').map(w => w.trim()) : null
      };
      const response = await axios.post(`${API}/incidents`, data);
      toast.success(`Incident ${response.data.incident_number} reported`);
      setCreateDialog(false);
      setFormData({
        category: 'Other',
        severity: 'medium',
        title: '',
        description: '',
        location_detail: '',
        occurred_at: getISTDateTime(),
        witnesses: '',
        immediate_action: '',
        estimated_loss: 0
      });
      fetchIncidents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    }
  };

  const handleUpdate = async () => {
    if (!selectedIncident) return;
    try {
      await axios.put(`${API}/incidents/${selectedIncident.id}`, updateData);
      toast.success('Incident updated');
      fetchIncidentDetail(selectedIncident.id);
      fetchIncidents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const formatCurrency = (value) => `₹${(value || 0).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-6" data-testid="incidents-tab">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Incident Reporting</h3>
          <p className="text-muted text-sm">Track and manage safety incidents</p>
        </div>
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="report-incident-btn">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Report Incident
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Report New Incident</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Severity *</label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief description of the incident"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Description *</label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of what happened"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Location Detail</label>
                  <Input
                    value={formData.location_detail}
                    onChange={(e) => setFormData({ ...formData, location_detail: e.target.value })}
                    placeholder="e.g., Near Dispenser #3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">When it occurred *</label>
                  <Input
                    type="datetime-local"
                    value={formData.occurred_at}
                    onChange={(e) => setFormData({ ...formData, occurred_at: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Witnesses</label>
                  <Input
                    value={formData.witnesses}
                    onChange={(e) => setFormData({ ...formData, witnesses: e.target.value })}
                    placeholder="Comma-separated names"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Estimated Loss (₹)</label>
                  <Input
                    type="number"
                    value={formData.estimated_loss}
                    onChange={(e) => setFormData({ ...formData, estimated_loss: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Immediate Action Taken</label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    rows={2}
                    value={formData.immediate_action}
                    onChange={(e) => setFormData({ ...formData, immediate_action: e.target.value })}
                    placeholder="What steps were taken immediately"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">Report Incident</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs text-muted">Open</p>
              <p className="text-xl font-bold">{stats.by_status?.open || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-muted">Investigating</p>
              <p className="text-xl font-bold">{stats.by_status?.investigating || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-muted">Resolved</p>
              <p className="text-xl font-bold">{stats.by_status?.resolved || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-xs text-muted">Closed</p>
              <p className="text-xl font-bold">{stats.by_status?.closed || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <p className="text-xs text-red-600 dark:text-red-400">Total Est. Loss</p>
          <p className="text-xl font-bold text-red-700 dark:text-red-300">{formatCurrency(stats.total_estimated_loss)}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-secondary/30 rounded-lg">
        <Filter className="w-4 h-4 text-muted" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="investigating">Investigating</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Incidents List */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Incident #</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Occurred</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-muted uppercase">Severity</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-muted uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Est. Loss</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-muted uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {incidents.map((incident) => {
              const CategoryIcon = categoryIcons[incident.category] || categoryIcons.default;
              return (
                <tr key={incident.id} className="hover:bg-secondary/50" data-testid={`incident-row-${incident.id}`}>
                  <td className="px-4 py-3 text-sm font-medium">{incident.incident_number}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <CategoryIcon className="w-4 h-4 text-muted" />
                      {incident.category}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-xs truncate">{incident.title}</td>
                  <td className="px-4 py-3 text-sm">{new Date(incident.occurred_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${severityColors[incident.severity]}`}>
                      {incident.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[incident.status]}`}>
                      {incident.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(incident.estimated_loss)}</td>
                  <td className="px-4 py-3 text-center">
                    <Button size="sm" variant="ghost" onClick={() => fetchIncidentDetail(incident.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {incidents.length === 0 && (
          <div className="text-center py-12 text-muted">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No incidents reported</p>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incident Details</DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold">{selectedIncident.incident_number}</h3>
                  <p className="text-lg">{selectedIncident.title}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-sm font-semibold ${severityColors[selectedIncident.severity]}`}>
                    {selectedIncident.severity}
                  </span>
                  <span className={`ml-2 px-2 py-1 rounded text-sm font-semibold ${statusColors[selectedIncident.status]}`}>
                    {selectedIncident.status}
                  </span>
                </div>
              </div>

              <Card className="p-4 border border-border">
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-sm">{selectedIncident.description}</p>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted">Category</p>
                  <p className="font-medium">{selectedIncident.category}</p>
                </div>
                <div>
                  <p className="text-sm text-muted">Occurred At</p>
                  <p className="font-medium">{new Date(selectedIncident.occurred_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</p>
                </div>
                {selectedIncident.location_detail && (
                  <div>
                    <p className="text-sm text-muted">Location</p>
                    <p className="font-medium">{selectedIncident.location_detail}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted">Estimated Loss</p>
                  <p className="font-medium text-red-600">{formatCurrency(selectedIncident.estimated_loss)}</p>
                </div>
                {selectedIncident.witnesses?.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted">Witnesses</p>
                    <p className="font-medium">{selectedIncident.witnesses.join(', ')}</p>
                  </div>
                )}
                {selectedIncident.immediate_action && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted">Immediate Action</p>
                    <p className="font-medium">{selectedIncident.immediate_action}</p>
                  </div>
                )}
              </div>

              {/* Update Form */}
              <Card className="p-4 border border-border bg-secondary">
                <h4 className="font-semibold mb-3">Update Incident</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="investigating">Investigating</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Root Cause</label>
                    <textarea
                      className="w-full px-3 py-2 border border-border rounded-md bg-background"
                      rows={2}
                      value={updateData.root_cause}
                      onChange={(e) => setUpdateData({ ...updateData, root_cause: e.target.value })}
                      placeholder="What caused this incident?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Corrective Action</label>
                    <textarea
                      className="w-full px-3 py-2 border border-border rounded-md bg-background"
                      rows={2}
                      value={updateData.corrective_action}
                      onChange={(e) => setUpdateData({ ...updateData, corrective_action: e.target.value })}
                      placeholder="What actions will prevent recurrence?"
                    />
                  </div>
                  <Button onClick={handleUpdate} className="w-full">Update Incident</Button>
                </div>
              </Card>

              {selectedIncident.resolved_at && (
                <div className="text-sm text-muted">
                  Resolved: {new Date(selectedIncident.resolved_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                </div>
              )}
              {selectedIncident.closed_at && (
                <div className="text-sm text-muted">
                  Closed: {new Date(selectedIncident.closed_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default IncidentsTab;
